/**
 * Static Media Configuration for Nginx + Cloudflare CDN
 * 
 * Serves immutable public assets from 'media' bucket via CDN to reduce latency by 90%
 * (15-30ms with Cloudflare cache hit vs 150-300ms direct Supabase)
 * 
 * @module staticMedia
 */

// Use CDN in production, Supabase in development
const USE_CDN = import.meta.env.PROD;

export const STATIC_MEDIA_CONFIG = {
  // Nginx + Cloudflare CDN URL (production)
  CDN_BASE_URL: 'https://storage.interlaced.app/media',
  
  // Fallback to Supabase (development/staging)
  SUPABASE_BASE_URL: 'https://ohllmsaklzfbepnivvfc.supabase.co/storage/v1/object/public/media',
  
  // Feature flag for easy rollback
  USE_CDN,
} as const;

/**
 * Get URL for static media file
 * @param filename - Name of the file (e.g., 'date-1.jpg', 'og-image.jpg')
 * @returns Full URL to the file via CDN (prod) or Supabase (dev)
 * 
 * @example
 * getStaticMediaUrl('date-1.jpg') 
 * // => 'https://storage.interlaced.app/media/date-1.jpg' (prod)
 * // => 'https://ohllmsaklzfbepnivvfc.supabase.co/storage/v1/object/public/media/date-1.jpg' (dev)
 */
export const getStaticMediaUrl = (filename: string): string => {
  const baseUrl = STATIC_MEDIA_CONFIG.USE_CDN 
    ? STATIC_MEDIA_CONFIG.CDN_BASE_URL 
    : STATIC_MEDIA_CONFIG.SUPABASE_BASE_URL;
  
  // Remove leading slash if present to avoid double slashes
  const cleanFilename = filename.startsWith('/') ? filename.slice(1) : filename;
  
  return `${baseUrl}/${cleanFilename}`;
};

/**
 * Static media files available on CDN
 * Centralized list for type safety and easy updates
 */
export const STATIC_MEDIA_FILES = {
  // Lifestyle images
  DATE_1: 'date-1.jpg',
  DATE_2: 'date-2.jpg',
  DATE_3: 'date-3.jpg',
  
  // Onboarding placeholders
  CASUAL_PLACEHOLDER: 'casual-placeholder.jpg',
  SERIOUS_PLACEHOLDER: 'serious-placeholder.jpg',
  SPONSOR_PLACEHOLDER: 'sponsor-placeholder.jpg',
  SPONSORED_PLACEHOLDER: 'sponsored-placeholder.jpg',
  
  // Meta/SEO
  OG_IMAGE: 'interlaced-og-image.jpg',
} as const;

// Type for valid static media filenames
export type StaticMediaFile = typeof STATIC_MEDIA_FILES[keyof typeof STATIC_MEDIA_FILES];
